import React, { useState, useEffect } from 'react';
import { useLanguage } from './LanguageContext';
import { apiGet, apiDelete, API_URL } from '../utils/api';
import BranchForm from './BranchForm';
import Pagination from './Pagination';

const Branches = ({ admin }) => {
  const { print_lang } = useLanguage();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    fetchBranches();
  }, []);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const fetchBranches = async () => {
    try {
      const data = await apiGet('/api/admin/branches');
      if (data.success) {
        setBranches(data.data);
      }
    } catch (err) {
      setError(err.message || 'Failed to load branches');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingBranch(null);
    setShowModal(true);
  };

  const handleEdit = (branch) => {
    setEditingBranch(branch);
    setShowModal(true);
  };

  const handleDelete = async (branch) => {
    if (!window.confirm(print_lang('common.confirmDelete', 'Are you sure you want to delete?'))) {
      return;
    }

    try {
      const data = await apiDelete(`/api/admin/branches/${branch.branch_id}`);
      if (data.success) {
        fetchBranches();
      } else {
        alert(data.message || 'Delete failed');
      }
    } catch (err) {
      alert(err.message || 'Failed to delete branch');
    }
  };

  const handleSave = () => {
    setShowModal(false);
    fetchBranches();
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  // Filter branches by search term
  const filteredBranches = branches.filter(branch => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      branch.name?.toLowerCase().includes(search) ||
      branch.address?.toLowerCase().includes(search) ||
      branch.phone?.toLowerCase().includes(search)
    );
  });

  // Paginate
  const totalCount = filteredBranches.length;
  const totalPages = Math.ceil(totalCount / pageSize);
  const paginatedBranches = filteredBranches.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">{print_lang('nav.branches', 'Branches')}</h1>
        <button className="btn btn-primary" onClick={handleAdd}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          {print_lang('branch.addBranch', 'Add Branch')}
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Search */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label">{print_lang('common.search', 'Search')}</label>
          <input
            type="text"
            className="form-input"
            placeholder="Search by name, address, phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="card">
        {paginatedBranches.length === 0 && !searchTerm ? (
          <div className="empty-state">
            <svg className="empty-state-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
            <h3 className="empty-state-title">{print_lang('common.noData', 'No data found')}</h3>
            <p className="empty-state-text">Get started by adding your first branch</p>
            <button className="btn btn-primary" onClick={handleAdd}>
              {print_lang('branch.addBranch', 'Add Branch')}
            </button>
          </div>
        ) : paginatedBranches.length === 0 && searchTerm ? (
          <div className="empty-state">
            <svg className="empty-state-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <h3 className="empty-state-title">{print_lang('common.noData', 'No data found')}</h3>
            <p className="empty-state-text">No branches match your search</p>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: '50px' }}>Logo</th>
                    <th>{print_lang('branch.name', 'Branch Name')}</th>
                    <th>{print_lang('branch.address', 'Address')}</th>
                    <th>{print_lang('branch.phone', 'Phone')}</th>
                    <th>{print_lang('branch.userCount', 'Users')}</th>
                    <th>{print_lang('branch.dentistCount', 'Dentists')}</th>
                    <th>{print_lang('branch.dentistLimit', 'Limit')}</th>
                    <th>{print_lang('common.actions', 'Actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedBranches.map((branch) => (
                    <tr key={branch.branch_id}>
                      <td>
                        {branch.logo_path ? (
                          <img
                            src={`${API_URL}/${branch.logo_path}`}
                            alt="Logo"
                            style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }}
                          />
                        ) : (
                          <div style={{
                            width: '40px',
                            height: '40px',
                            backgroundColor: '#e5e7eb',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#9ca3af'
                          }}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                              <circle cx="8.5" cy="8.5" r="1.5" />
                              <polyline points="21 15 16 10 5 21" />
                            </svg>
                          </div>
                        )}
                      </td>
                      <td>
                        <strong>{branch.name}</strong>
                      </td>
                      <td>{branch.address || '-'}</td>
                      <td>{branch.phone || '-'}</td>
                      <td>
                        <span className="badge badge-info">{branch.user_count || 0}</span>
                      </td>
                      <td>
                        <span className="badge badge-violet">{branch.dentist_count || 0}</span>
                      </td>
                      <td>
                        <span className="badge badge-warning">{branch.dentist_limit || 10}</span>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button className="btn btn-sm btn-outline" onClick={() => handleEdit(branch)}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                          </button>
                          <button className="btn btn-sm btn-danger" onClick={() => handleDelete(branch)}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="3 6 5 6 21 6" />
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={totalCount}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          </>
        )}
      </div>

      {showModal && (
        <BranchForm
          branch={editingBranch}
          admin={admin}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

export default Branches;
